import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import UserLayout from "../components/custom/layouts/UserLayout";
import { apiUrl } from "../config";
import { AuthUserType } from "../redux/actions/authUserActions";
import {
  ClientRegistrationType,
  fetchClientRegistrationAction,
} from "../redux/actions/clientRegistration";
import { AppDispatch, StoreState } from "../redux/store";
import "./Profile.css";

const displayValue = (value?: string | number | null): string => {
  const text = value != null ? String(value).trim() : "";
  return text || "—";
};

const imageSrc = (path?: string | null): string | null => {
  if (!path || !String(path).trim()) return null;
  const normalized = String(path).replace(/^\/+/, "");
  return `${apiUrl}/${normalized}`;
};

interface BioFieldProps {
  label: string;
  value?: string | number | null;
}

const BioField = ({ label, value }: BioFieldProps) => {
  const text = displayValue(value);
  const isEmpty = text === "—";

  return (
    <div className="profile-field">
      <span className="profile-field-label">{label}</span>
      <span
        className={`profile-field-value${isEmpty ? " profile-field-value--empty" : ""}`}
      >
        {text}
      </span>
    </div>
  );
};

interface BioSectionProps {
  title: string;
  icon: string;
  children: React.ReactNode;
}

const BioSection = ({ title, icon, children }: BioSectionProps) => (
  <section className="profile-section">
    <h4 className="profile-section-title">
      <i className={icon} aria-hidden />
      {title}
    </h4>
    {children}
  </section>
);

const Profile = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(true);
  const clients = useSelector<StoreState, ClientRegistrationType[]>(
    (state) => state.clientRegistration,
  );
  const authUser = useSelector<StoreState, AuthUserType | null>(
    (state) => state.authUser,
  );

  const client = clients[0] ?? null;
  const logoSrc = imageSrc(client?.shopLogo || client?.shopPhoto);
  const bannerSrc = imageSrc(client?.bannerImage || client?.bannerPhoto);
  const shopPhotoSrc = imageSrc(client?.shopPhoto);

  useEffect(() => {
    void dispatch(fetchClientRegistrationAction()).finally(() => setLoading(false));
  }, [dispatch]);

  const headOfficeLabel =
    client?.headOffice === "yes"
      ? "Yes"
      : client?.headOffice === "no"
        ? "No"
        : displayValue(client?.headOffice);

  return (
    <UserLayout>
      <div className="profile-page">
        <div className="profile-toolbar">
          <p className="text-muted mb-0 small">
          </p>
          <Link to="/clientRegistration" className="profile-edit-link">
            <i className="fe-edit" aria-hidden />
            Manage registration
          </Link>
        </div>

        {loading ? (
          <div className="profile-biodata-card">
            <div className="profile-loading">
              <div className="spinner-border mb-3" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
              <p className="mb-0">Loading business profile…</p>
            </div>
          </div>
        ) : !client ? (
          <div className="profile-biodata-card">
            <div className="profile-empty">
              <i className="fe-alert-circle fs-2 d-block mb-2 text-muted" aria-hidden />
              <p className="mb-2 fw-semibold">No client registration found</p>
              <p className="mb-0 small">
                Add your business details from the client registration page.
              </p>
            </div>
          </div>
        ) : (
          <div className="profile-biodata-card">
            <header className="profile-biodata-header">
              <div className="profile-photo-wrap">
                {logoSrc ? (
                  <img src={logoSrc} alt={client.shopFullName || "Shop logo"} />
                ) : (
                  <span className="profile-photo-placeholder">
                    {(client.shopFullName || "S").charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="profile-identity">
                <h3>{displayValue(client.shopFullName)}</h3>
                {client.shopShortName ? (
                  <p className="profile-short-name">{client.shopShortName}</p>
                ) : null}
                {client.tagLine ? (
                  <p className="profile-tagline">&ldquo;{client.tagLine}&rdquo;</p>
                ) : null}
                <div className="profile-badges">
                  {client.clientType ? (
                    <span className="profile-badge">
                      <i className="fe-briefcase" aria-hidden />
                      {client.clientType}
                    </span>
                  ) : null}
                  {client.proprietorName ? (
                    <span className="profile-badge">
                      <i className="fe-user" aria-hidden />
                      {client.proprietorName}
                    </span>
                  ) : null}
                  {client.contactNo1 ? (
                    <span className="profile-badge">
                      <i className="fe-phone" aria-hidden />
                      +91 {client.contactNo1}
                    </span>
                  ) : null}
                </div>
              </div>
            </header>

            <BioSection title="Business Information" icon="fe-home">
              <div className="profile-field-grid">
                <BioField label="Shop full name" value={client.shopFullName} />
                <BioField label="Shop short name" value={client.shopShortName} />
                <BioField label="Client type" value={client.clientType} />
                <BioField label="Head office" value={headOfficeLabel} />
                <BioField label="Proprietor name" value={client.proprietorName} />
                <BioField label="Tag line" value={client.tagLine} />
              </div>
            </BioSection>

            <BioSection title="Tax & Registration" icon="fe-file-text">
              <div className="profile-field-grid">
                <BioField label="GST number" value={client.gstNo} />
                <BioField label="PAN card number" value={client.panCardNo} />
                <BioField label="Licence no. 1" value={client.licenceNo1} />
                <BioField label="Licence no. 2" value={client.licenceNo2} />
                <BioField label="Licence no. 3" value={client.licenceNo3} />
              </div>
            </BioSection>

            <BioSection title="Address" icon="fe-map-pin">
              <div className="profile-field-grid">
                <BioField label="Address line 1" value={client.address1} />
                <BioField label="Address line 2" value={client.address2} />
                <BioField label="Place" value={client.place} />
                <BioField label="Taluk" value={client.taluk} />
                <BioField label="District" value={client.district} />
                <BioField label="State" value={client.state} />
                <BioField label="Pincode" value={client.pincode} />
              </div>
            </BioSection>

            <BioSection title="Contact" icon="fe-phone">
              <div className="profile-field-grid">
                <BioField label="Primary contact" value={client.contactNo1} />
                <BioField label="Secondary contact" value={client.contactNo2} />
              </div>
            </BioSection>

            {(logoSrc || shopPhotoSrc || bannerSrc) && (
              <BioSection title="Branding & Media" icon="fe-image">
                <div className="profile-media-grid">
                  {logoSrc ? (
                    <div className="profile-media-item">
                      <img src={logoSrc} alt="Shop logo" />
                      <div className="profile-media-caption">Shop logo</div>
                    </div>
                  ) : null}
                  {shopPhotoSrc && shopPhotoSrc !== logoSrc ? (
                    <div className="profile-media-item">
                      <img src={shopPhotoSrc} alt="Shop photo" />
                      <div className="profile-media-caption">Shop photo</div>
                    </div>
                  ) : null}
                  {bannerSrc ? (
                    <div className="profile-media-item profile-media-item--banner">
                      <img src={bannerSrc} alt="Banner" />
                      <div className="profile-media-caption">Banner image</div>
                    </div>
                  ) : null}
                </div>
              </BioSection>
            )}
          </div>
        )}
      </div>
    </UserLayout>
  );
};

export default Profile;
